import * as React from 'react';
import { Dropdown } from 'react-bootstrap';
import clsx from 'clsx';
import styled from 'styled-components';
import { Button } from '../Button';
import { LabeledInput } from '../LabeledInput';
import FilterTag from './FilterTag';
import { SvgImage } from '../SvgImage';
import { getAssetPath } from '../../util/images';
import {
  ITableFilterState,
  ITableFilterProps,
  ICustomToggleFilterProps
} from './TableFilter.types';
import {
  onToggle,
  onSaveClick,
  onRemoveTag,
  onUpdateFilter
} from './TableFilter.logic';
import { NoneButton } from '../AttachmentTag/AttachmentTag';

const TableFilterButton = styled(SvgImage)`
  svg path {
    stroke: ${({ theme }) => theme.colors.primary2};
  }
`;

const TableFilterWrapper = styled.div`
  background: transparent;
  .dropdown-menu {
    margin: 15px 0 0 0;
    padding: 24px;
  }
  .dropdown {
    display: inline;
  }
`;

const FilterButton = styled.button<{ active?: boolean }>`
  color: ${({ theme }) => theme.colors.font1};
  margin: 0 24px 0 0;
  font-style: normal;
  font-weight: 500;
  font-size: 14px;
  line-height: 17px;
  align-items: center;
  padding: 8px 16px;
  border: none;
  background-color: ${({ theme }) => theme.colors.white};
  border-radius: 4px;
  cursor: pointer;
  &:hover {
    background-color: ${({ theme }) => theme.colors.white};
  }
  div {
    display: inline;
    padding-right: 6px;
  }
`;

const FilterElement = styled(LabeledInput)`
  margin-bottom: 16px;
`;

const TagClearButton = styled(NoneButton)`
  margin-left: 16px;
  color: ${({ theme }) => theme.colors.primary2};
`;

export const ClearButton = styled(NoneButton)`
  position: absolute;
  right: 0;
  top: -8px;
  color: ${({ theme }) => theme.colors.primary2};
`;

export const TableFilterForm = styled.form`
  position: relative;
`;

const ButtonsWrapper = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
`;

const ButtonSpacer = styled.div`
  height: 100%;
  width: 20px;
`;

const CustomToggleFilter = React.forwardRef<HTMLButtonElement>(
  (props: ICustomToggleFilterProps, buttonRef) => {
    const { children, onClick, active, testId } = props;
    return (
      <FilterButton
        className={clsx({
          active
        })}
        type="button"
        onClick={e => {
          e.preventDefault();
          onClick(e);
        }}
        ref={buttonRef}
        data-testid={testId || 'filter-button'}
      >
        {children}
      </FilterButton>
    );
  }
);

const TableFilter: React.FC<ITableFilterProps> = (props: ITableFilterProps) => {
  const {
    className,
    options,
    onSave,
    componentTestId,
    clearButtonId,
    cancelButtonId,
    saveButtonId,
    tagsClearButtonId
  } = props;
  const [state, setState] = React.useState<ITableFilterState>({
    show: false,
    filter: {},
    tags: []
  });

  const handleUpdateFilter = (key: string, value: string) =>
    onUpdateFilter(key, value, state, setState);
  const handleOnToggle = (e: boolean) => onToggle(e, state, setState);
  const handleOnSaveClick = () => onSaveClick(onSave, state, setState);
  const handleOnRemoveTag = (key: string) =>
    onRemoveTag(key, onSave, state, setState);

  return (
    <TableFilterWrapper
      className={`${className}`}
      data-testid={componentTestId || 'filter-component'}
    >
      <Dropdown show={state.show} onToggle={handleOnToggle}>
        <Dropdown.Toggle
          as={CustomToggleFilter}
          id="filters-button"
          active={state.show}
        >
          <TableFilterButton inAssetsPath={getAssetPath('icons/filter.svg')} />
          Filters
        </Dropdown.Toggle>
        <Dropdown.Menu>
          <TableFilterForm>
            <ClearButton
              type="button"
              className="small-text"
              data-testid={clearButtonId}
              onClick={() => {
                onSave({});
                setState({ ...state, tags: [], filter: {} });
              }}
            >
              Clear all
            </ClearButton>
            {options &&
              options.map(
                ({ key, testId, label, placeholder, customComponent }) => {
                  return customComponent ? (
                    customComponent(state, value =>
                      handleUpdateFilter(key, value)
                    )
                  ) : (
                    <FilterElement
                      value={state.filter && state.filter[key]}
                      key={key}
                      label={label}
                      placeholder={placeholder}
                      onChange={value => {
                        handleUpdateFilter(key, value);
                      }}
                      type="text"
                      testId={testId}
                    />
                  );
                }
              )}
            <ButtonsWrapper>
              <Button
                colorType="white"
                type="button"
                text="Cancel"
                onClick={() => setState({ ...state, filter: {}, show: false })}
                testId={cancelButtonId || 'filter-cancel'}
                width={80}
                height={32}
              />
              <ButtonSpacer />
              <Button
                colorType="primary"
                type="submit"
                text="Save"
                onClick={handleOnSaveClick}
                testId={saveButtonId || 'filter-save'}
                width={80}
                height={32}
              />
            </ButtonsWrapper>
          </TableFilterForm>
        </Dropdown.Menu>
      </Dropdown>
      {state.tags && state.tags.length ? (
        <>
          {state.tags.map(f => {
            const [key, value] = f;
            return (
              <FilterTag
                key={key}
                filterKey={key}
                value={(value.label as string) || (value as string)}
                onRemoveTag={handleOnRemoveTag}
              />
            );
          })}
          <TagClearButton
            data-testid={tagsClearButtonId || 'filter_tags_clear_button'}
            type="button"
            className="text-regular"
            onClick={() => {
              onSave({});
              setState({ ...state, tags: [], filter: {} });
            }}
          >
            Clear all
          </TagClearButton>
        </>
      ) : null}
    </TableFilterWrapper>
  );
};

export default TableFilter;
